import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, ArrowDown, FileText, TrendingUp, Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { LineChart, Line, YAxis, ResponsiveContainer } from 'recharts';
import { useTradingAccounts } from '@/api/trading-accounts/use-trading-accounts';
import { useOpenPositions } from '@/api/positions/use-positions';
import { useTraderInstruments } from '@/api/instruments/use-instruments';
import { useAllPrices } from '@/hooks/useAllPrices';
import httpClient from '@/api/httpClient';
import { API_ENDPOINTS } from '@/api/endpoints';
import PriceChangeDisplay from '@/components/mobile/PriceChangeDisplay';
import MobileFloatingNav from '@/components/mobile/MobileFloatingNav';
import { GlassCard, QuickActions, QuickAction } from '@/components/trader/TraderUI';
import { formatCurrencyAmount } from '@/utils/formatters';

interface SparklinePairCardProps {
    instrumentId: string;
    symbol: string;
    price: string;
    onClick: () => void;
}

const SparklinePairCard: React.FC<SparklinePairCardProps> = ({
    instrumentId,
    symbol,
    price,
    onClick,
}) => {
    const { data: sparkPoints } = useQuery<number[]>({
        queryKey: ['sparkline', instrumentId],
        queryFn: async () => {
            const to = Math.floor(Date.now() / 1000);
            const from = to - 8 * 60 * 60; // last 8 hours
            const { data } = await httpClient.get<{ s: string; c?: number[] }>(
                API_ENDPOINTS.TRADER.CHART_HISTORY,
                { params: { symbol, resolution: '60', from, to } }
            );
            return data.s === 'ok' && data.c ? data.c : [];
        },
        staleTime: 60000,
        enabled: !!instrumentId && !!symbol,
    });

    const chartData = (sparkPoints ?? []).map((v, i) => ({ v, i }));
    const first = sparkPoints?.[0] ?? 0;
    const last = sparkPoints?.[sparkPoints.length - 1] ?? 0;
    const isPositive = last >= first;
    const lineColor = isPositive ? '#10b981' : '#ef4444';

    return (
        <div
            className="trader-glass rounded-3xl p-4 cursor-pointer hover:opacity-90 transition-opacity"
            onClick={onClick}
        >
            <div className="font-semibold text-sm mb-2">{symbol}</div>
            <div className="h-12 w-full my-2">
                {chartData.length > 1 ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData}>
                            <YAxis domain={['dataMin', 'dataMax']} hide />
                            <Line
                                type="monotone"
                                dataKey="v"
                                stroke={lineColor}
                                strokeWidth={2}
                                dot={false}
                                isAnimationActive={false}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="h-full flex items-center justify-center">
                        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                    </div>
                )}
            </div>
            <div className="text-sm font-bold">{price}</div>
            <PriceChangeDisplay
                instrumentId={instrumentId}
                currentPrice={parseFloat(price)}
                className="text-xs mt-1"
            />
        </div>
    );
};

const MobileHomePage: React.FC = () => {
    const { t } = useTranslation('mobile');
    const navigate = useNavigate();
    const { data: tradingAccounts, isLoading: isLoadingAccounts } = useTradingAccounts();
    const { data: positionsData, isLoading: isLoadingPositions } = useOpenPositions();
    const { data: instrumentsData, isLoading: isLoadingInstruments } = useTraderInstruments({
        per_page: 4,
        page: 1,
    });

    // Privacy feature: toggle balance visibility
    const [isBalanceVisible, setIsBalanceVisible] = useState(true);

    // Get all prices (useAllPrices now fetches all instruments automatically)
    const { prices: allPrices } = useAllPrices(undefined, 1000); // Poll every 1 second for real-time updates

    const isLoading = isLoadingAccounts || isLoadingPositions || isLoadingInstruments;

    // Calculate total equity and balance
    const totalEquity =
        tradingAccounts?.accounts?.reduce((sum, acc) => sum + Number(acc.equity || 0), 0) || 0;
    const totalBalance =
        tradingAccounts?.accounts?.reduce((sum, acc) => sum + Number(acc.balance || 0), 0) || 0;

    // Calculate daily P&L
    const dailyPnL =
        tradingAccounts?.accounts?.reduce((sum, acc) => sum + Number(acc.daily_pnl || 0), 0) || 0;

    // Get open positions
    const openPositions = positionsData?.data?.slice(0, 5) || [];

    // Get top instruments with prices
    const topPairs =
        instrumentsData?.data?.slice(0, 4).map(instrument => {
            const priceData = allPrices?.get(instrument.id);
            const currentPrice = priceData?.bid || 0;
            // We'll calculate change per instrument, but for simplicity in list view,
            // we'll use a simplified calculation or fetch individually
            // For now, using a placeholder that will be calculated by PriceChange component
            const change = 0; // Will be calculated per instrument if needed

            return {
                pair: instrument.symbol,
                price: currentPrice.toFixed(5),
                change: change,
                trend: change >= 0 ? 'up' : 'down',
                instrumentId: instrument.id,
            };
        }) || [];

    if (isLoading) {
        return (
            <div className="min-h-screen text-foreground flex items-center justify-center pb-20">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen text-foreground pb-32">
            <div className="px-4 pt-4">
                {/* Balance Card */}
                <GlassCard className="p-6 mb-6 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/30 rounded-full blur-3xl -mr-10 -mt-10 pointer-events-none" />
                    <div className="absolute bottom-0 left-0 w-32 h-32 bg-purple-500/30 rounded-full blur-3xl -ml-10 -mb-10 pointer-events-none" />

                    <div className="relative">
                        <div className="flex items-center justify-between mb-4">
                            <span className="text-muted-foreground text-sm font-medium">
                                {t('totalEquity')}
                            </span>
                            <button
                                onClick={() => setIsBalanceVisible(!isBalanceVisible)}
                                className="text-muted-foreground hover:text-foreground transition-colors"
                            >
                                {isBalanceVisible ? (
                                    <Eye className="w-5 h-5" />
                                ) : (
                                    <EyeOff className="w-5 h-5" />
                                )}
                            </button>
                        </div>
                        <div className="text-sm text-muted-foreground mb-2">
                            {t('allAccountsUsd')}
                        </div>
                        <div className="text-3xl font-bold mb-4 tracking-tight">
                            {isBalanceVisible ? formatCurrencyAmount(totalEquity, 'USD') : '••••••'}
                        </div>

                        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
                            <div>
                                <div className="text-xs text-muted-foreground mb-1">
                                    {t('balance')}
                                </div>
                                <div className="font-semibold">
                                    {isBalanceVisible
                                        ? formatCurrencyAmount(totalBalance, 'USD')
                                        : '••••••'}
                                </div>
                            </div>
                            <div>
                                <div className="text-xs text-muted-foreground mb-1">
                                    {t('pnlToday')}
                                </div>
                                <div
                                    className={`font-semibold ${
                                        dailyPnL >= 0
                                            ? 'text-trader-positive'
                                            : 'text-trader-negative'
                                    }`}
                                >
                                    {isBalanceVisible ? (
                                        <>
                                            {dailyPnL >= 0 ? '+' : ''}
                                            {formatCurrencyAmount(dailyPnL, 'USD')}
                                        </>
                                    ) : (
                                        '••••••'
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </GlassCard>

                {/* Action Buttons */}
                <QuickActions className="grid grid-cols-3 gap-3 mb-8">
                    <QuickAction
                        primary
                        icon={<TrendingUp className="w-6 h-6" />}
                        label={t('market')}
                        onClick={() => navigate('/market')}
                    />
                    <QuickAction
                        icon={<ArrowDown className="w-6 h-6" />}
                        label={t('deposit')}
                        onClick={() => navigate('/deposit-withdraw?action=deposit')}
                    />
                    <QuickAction
                        icon={<FileText className="w-6 h-6" />}
                        label={t('history')}
                        onClick={() => navigate('/history')}
                    />
                </QuickActions>

                {/* Open Positions */}
                {openPositions.length > 0 && (
                    <div className="mb-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold">{t('openPositions')}</h2>
                            <span className="text-sm text-muted-foreground">
                                {t('activeCount', { count: openPositions.length })}
                            </span>
                        </div>
                        <div className="space-y-3">
                            {openPositions.map((position: any) => {
                                const profit = Number(position.unrealized_pnl || 0);
                                const profitPositive = profit >= 0;
                                return (
                                    <div key={position.id} className="trader-glass rounded-2xl p-4">
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center gap-3">
                                                <div
                                                    className={`px-3 py-1 rounded-lg text-xs font-semibold ${
                                                        position.side === 'buy'
                                                            ? 'trader-pill-positive'
                                                            : 'trader-pill-negative'
                                                    }`}
                                                >
                                                    {position.side?.toUpperCase() || 'BUY'}
                                                </div>
                                                <div>
                                                    <div className="font-semibold">
                                                        {position.instrument?.symbol || 'N/A'}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground">
                                                        {t('volume')}:{' '}
                                                        {Number(position.lot_size || 0).toFixed(2)}
                                                    </div>
                                                </div>
                                            </div>
                                            <div
                                                className={`text-right ${
                                                    profitPositive
                                                        ? 'text-trader-positive'
                                                        : 'text-trader-negative'
                                                }`}
                                            >
                                                <div className="font-bold">
                                                    {profitPositive ? '+' : ''}
                                                    {formatCurrencyAmount(
                                                        profit,
                                                        position.trading_account?.base_currency ||
                                                            'USD'
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex justify-between text-xs text-muted-foreground pt-3 border-t border-border">
                                            <div>
                                                <span>{t('open')}: </span>
                                                <span className="text-foreground">
                                                    {Number(position.open_price || 0).toFixed(5)}
                                                </span>
                                            </div>
                                            <div>
                                                <span>{t('current')}: </span>
                                                <span className="text-foreground">
                                                    {Number(
                                                        position.current_price ||
                                                            position.open_price ||
                                                            0
                                                    ).toFixed(5)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Top Pairs — horizontal scroll with sparkline charts */}
                {topPairs.length > 0 && (
                    <div className="mb-6">
                        <h2 className="text-lg font-semibold mb-4">{t('topPairs')}</h2>
                        <div className="grid grid-cols-2 gap-3">
                            {topPairs.map((pair, index) => (
                                <SparklinePairCard
                                    key={pair.instrumentId}
                                    instrumentId={pair.instrumentId}
                                    symbol={pair.pair}
                                    price={pair.price}
                                    onClick={() =>
                                        navigate(`/trade/${pair.instrumentId}`, {
                                            state: {
                                                instrument: instrumentsData?.data?.[index],
                                            },
                                        })
                                    }
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <MobileFloatingNav />
        </div>
    );
};

export default MobileHomePage;
